import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/*
 * Mobile header collapse (< 992px).
 *
 * Below the `nav` breakpoint the header is a black top bar (FPFF LIVE /
 * calendar / PROGRAM) that scrolls away, plus a sticky white
 * `.header-mobile-bar` (brand + hamburger). While the top bar is still on
 * screen the brand shows the full logo; once the user scrolls past it the logo
 * gives way to the compact hero.
 *
 * This module owns only the *trigger*: it toggles `html.is-header-collapsed`
 * once the top bar has scrolled out. All the actual motion (logo→hero swap,
 * offcanvas padding) is CSS keyed off that class — see interactions.css. The
 * complementary state, `html.is-header-menu-open`, is set in header_full.php.
 *
 * Mirrors headerNavBrand.ts: idempotent init that reverts the previous setup,
 * gated to the mobile breakpoint via gsap.matchMedia so it auto-disables on
 * desktop and re-arms on resize.
 */

const COLLAPSED = 'is-header-collapsed'

let mm: gsap.MatchMedia | null = null

export const destroyHeaderMobile = () => {
  if (mm) {
    mm.revert()
    mm = null
  }
  document.documentElement.classList.remove(COLLAPSED)
}

export const initHeaderMobile = () => {
  destroyHeaderMobile()

  mm = gsap.matchMedia()
  mm.add('(width < 62rem)', () => {
    const topBar = document.querySelector<HTMLElement>('.header-mobile-top')
    if (!topBar) return

    const root = document.documentElement
    const trigger = ScrollTrigger.create({
      trigger: topBar,
      // Collapse once the top bar's bottom edge reaches the viewport top —
      // i.e. it has fully scrolled away and the sticky bar has docked.
      start: 'bottom top',
      end: '+=99999',
      onEnter: () => root.classList.add(COLLAPSED),
      onLeaveBack: () => root.classList.remove(COLLAPSED),
    })

    // Sync the initial state (e.g. landing already scrolled, or after an ASwap).
    if (topBar.getBoundingClientRect().bottom <= 0) {
      root.classList.add(COLLAPSED)
    }

    return () => {
      trigger.kill()
      root.classList.remove(COLLAPSED)
    }
  })
}
